import {
    FC,
    HTMLAttributes,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import {FieldError} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import clsx from 'clsx';
import Icon from 'components/Icon';
import {IconType} from 'components/Icon/types';
import Overlay from 'components/Layout/Overlay';
import Pill from 'components/Pill';
import {useTooltip} from 'components/Tooltip/hook';
import useOutsideDetector from 'hooks/useOutsideDetector';
import {uniq} from 'lodash';
import {Option} from 'types';
import DropdownSelectItem from '../DropdownSelectItem';
import CLEAR_SELECTION from '../DropdownSelectItem/types';
import DropdownSelectSearch from '../DropdownSelectSearch';
import {select} from './styles.module.css';

type MultiChoice = {
    isMultiChoices: true;
    onChange?: (values: string[] | undefined) => void;
    value?: string[];
};

type SingleChoice = {
    isMultiChoices?: false;
    onChange?: (values: string | undefined) => void;
    value?: string;
};

export type BaseDropdownProps = {
    disabled?: boolean;
    error?: FieldError;
    hasClearSelection?: boolean;
    icon?: IconType;
    isCompact?: boolean;
    isSearchable?: boolean;
    labelClassName?: string;
    menuClassName?: string;
    options?: Array<Option>;
    placeholder?: string;
    title?: string;
} & (MultiChoice | SingleChoice) &
    Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>;

export type BaseDropdownSelectRef = {
    setValue: (data?: string | string[]) => void;
};

const BaseDropdownSelect: FC<BaseDropdownProps> = ({
    disabled,
    error,
    hasClearSelection = true,
    icon,
    isCompact,
    isMultiChoices,
    isSearchable,
    labelClassName,
    menuClassName,
    onChange,
    options = [],
    placeholder,
    title,
    value,
    ...rest
}) => {
    const {t} = useTranslation();
    const refSelect = useRef<HTMLDivElement>(null);
    const refPortal = useRef<HTMLDivElement>(null);

    const [show, setShow] = useState(() => false);
    const [optionsList, setOptionsList] = useState(() => options);

    const focusInput = useCallback(() => {
        refPortal.current?.querySelector('input')?.focus({
            preventScroll: true,
        });
    }, [refPortal]);
    const blurInput = useCallback(() => {
        refPortal.current?.querySelector('input')?.blur();
    }, [refPortal]);
    useEffect(() => {
        if (show) {
            setTimeout(() => focusInput(), 100);
        } else blurInput();
    }, [blurInput, focusInput, show]);

    const [searchQuery, setSearchQuery] = useState<string>('');

    useOutsideDetector([refSelect, refPortal], () => setShow(false));

    const getFilteredOptions = (input: Array<Option>) => {
        if (searchQuery.length === 0) return input;

        return input.filter(
            (option) =>
                typeof option.label === 'string' &&
                option.label.toLowerCase().includes(searchQuery.toLowerCase()),
        );
    };

    const isSelected = (inputValue: string) => {
        if (Array.isArray(value)) {
            return value.includes(inputValue);
        }

        return inputValue === value;
    };

    const filteredOptions = getFilteredOptions(optionsList);
    const selection = optionsList.filter((option) => isSelected(option.value));

    const {setTooltipRef} = useTooltip();

    useEffect(() => {
        if (refSelect.current) setTooltipRef(refSelect.current);
    }, [refSelect, setTooltipRef]);

    useEffect(() => {
        setSearchQuery('');
    }, [show]);

    const getDisplayTitle = () => {
        if (selection.length <= 0) {
            return title || placeholder || t('form:select');
        }

        if (isMultiChoices) {
            return t('dropdownSelect.itemSelected', {
                count: selection.length,
            });
        }

        return selection[0].label;
    };

    const onUserKeyPress = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            setShow(false);
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', onUserKeyPress);

        return () => {
            window.removeEventListener('keydown', onUserKeyPress);
        };
    }, []);

    const onClickSelect = () => {
        if (!disabled) setShow((prevState) => !prevState);

        if (isMultiChoices) {
            setOptionsList([
                // Selected Options
                ...options.filter((option) => isSelected(option.value)),
                // Unselected Options
                ...options.filter((option) => !isSelected(option.value)),
            ]);
        }
    };

    const onClickOption = (clickedValue: string) => {
        focusInput();

        if (clickedValue === CLEAR_SELECTION) {
            setSearchQuery('');
            onChange?.(undefined);

            return;
        }

        if (onChange) {
            if (isMultiChoices) {
                const currentValues = value || [];

                if (currentValues.includes(clickedValue)) {
                    onChange(
                        currentValues.filter(
                            (optionValue) => optionValue !== clickedValue,
                        ),
                    );
                } else {
                    onChange(uniq([...currentValues, clickedValue]));
                }
            } else {
                onChange(clickedValue as never);
                setShow(false);
            }
        }
    };

    const iconComponent = icon && <Icon icon={icon} size="base" />;

    const arrowComponent = (
        <Icon icon={show ? 'small_arrow_up' : 'small_arrow_down'} size="base" />
    );

    return (
        <>
            <div
                ref={refSelect}
                aria-controls="dropdown-select"
                aria-expanded={show}
                className={clsx(
                    select,
                    isCompact && 'h-8',
                    'flex w-full grow cursor-pointer select-none items-center justify-between gap-2 px-3 py-2',
                    labelClassName,
                )}
                data-disabled={disabled}
                data-dropdown={true}
                data-invalid={!!error}
                data-status={selection.length > 0 ? 'selected' : undefined}
                onClick={onClickSelect}
                onKeyDown={(event) =>
                    !disabled &&
                    event.key === 'Enter' &&
                    setShow((prevState) => !prevState)
                }
                role="combobox"
                tabIndex={0}
                {...rest}
            >
                <div className="flex items-center gap-2 truncate">
                    {iconComponent}
                    <span
                        className={clsx(
                            'truncate',
                            isMultiChoices &&
                                selection.length > 0 &&
                                clsx(
                                    'rounded px-2',
                                    disabled
                                        ? 'bg-grey-200 text-grey-500'
                                        : 'bg-grey-800 text-dark-primary',
                                ),
                        )}
                    >
                        {getDisplayTitle()}
                    </span>
                    {!isMultiChoices && selection[0]?.status && (
                        <Pill
                            color={selection[0].status?.color ?? 'grey'}
                            label={selection[0].status.label}
                        />
                    )}
                </div>
                {arrowComponent}
            </div>
            {show && refSelect.current && (
                <Overlay
                    className="border border-blue-400 bg-dark-primary p-1 shadow-md"
                    element={refSelect.current}
                    layoutLayer="z-dropdown"
                    layoutMethod="bottom-or-top"
                    layoutOffset={8}
                    matchWidth={true}
                >
                    <div ref={refPortal}>
                        {isSearchable && show && (
                            <DropdownSelectSearch
                                onSearchOption={setSearchQuery}
                                searchQuery={searchQuery}
                            />
                        )}
                        {!filteredOptions || filteredOptions.length <= 0 ? (
                            <div className="pointer-events-auto px-3 py-1">
                                {t('noResultsFound')}
                            </div>
                        ) : (
                            <div className="pointer-events-auto max-h-60 overflow-y-auto">
                                {filteredOptions.map((option) => (
                                    <DropdownSelectItem
                                        key={option.value}
                                        aria-label={`${option.label}`}
                                        disabled={option.disabled}
                                        isCheckable={isMultiChoices || false}
                                        label={option.label}
                                        onClick={() =>
                                            onClickOption(option.value)
                                        }
                                        selected={isSelected(option.value)}
                                        status={option.status}
                                        value={option.value}
                                    />
                                ))}
                            </div>
                        )}
                        {options?.some((option) => isSelected(option.value)) &&
                            hasClearSelection && (
                                <DropdownSelectItem
                                    aria-label="clear-selection"
                                    className={clsx(
                                        'text-light-secondary',
                                        'hover:bg-grey-200',
                                        'active:bg-grey-300',
                                        'focus:bg-grey-200 focus:outline-none',
                                    )}
                                    isCheckable={false}
                                    label={t('dropdownSelect.clearSelection')}
                                    onClick={() =>
                                        onClickOption(CLEAR_SELECTION)
                                    }
                                    value={CLEAR_SELECTION}
                                />
                            )}
                    </div>
                </Overlay>
            )}
        </>
    );
};

export default BaseDropdownSelect;
